import React, { useEffect, useRef, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import SignatureCanvas from "react-signature-canvas";
import { handleGetRequest } from "../../services/GetTemplate";
import styles from "./receiverSelect.module.scss";

/**
 * Reusable receiver identification + signature capture, shared by the
 * airport-handover and in-store-pickup flows.
 *
 * The "client" option confirms the group/pickup's already-resolved client
 * (there's no concept of multiple people per client account in this system),
 * "agent" searches that client's registered agents (up to 2, added via
 * MyAccount/CustomerDetail), and "guest" is a free-text name for someone who
 * isn't a customer.
 *
 * Calls onChange({ receiver, signatureDataUrl, isValid }) on every change.
 */
const ReceiverSelect = ({ clientId, clientName, onChange }) => {
    const [type, setType] = useState("client");
    const [agents, setAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [guestName, setGuestName] = useState("");
    const [signatureDataUrl, setSignatureDataUrl] = useState(null);
    const signatureRef = useRef(null);

    useEffect(() => {
        const loadAgents = async () => {
            if (!clientId) return;
            const res = await handleGetRequest(`/users/${clientId}/agents`);
            setAgents(res?.data || []);
        };
        loadAgents();
    }, [clientId]);

    const buildReceiver = () => {
        if (type === "client") {
            return clientId ? { type: "client", clientId, name: clientName } : null;
        }
        if (type === "agent") {
            return selectedAgent
                ? { type: "agent", agentId: selectedAgent._id, name: selectedAgent.name, idLast6: selectedAgent.idLast6 }
                : null;
        }
        return guestName.trim() ? { type: "guest", name: guestName.trim() } : null;
    };

    // Re-emit whenever the underlying selection changes
    useEffect(() => {
        const receiver = buildReceiver();
        if (onChange) {
            onChange({
                receiver,
                signatureDataUrl,
                isValid: !!receiver && !!signatureDataUrl,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, selectedAgent, guestName, signatureDataUrl, clientId]);

    const clearSignature = () => {
        if (signatureRef.current) signatureRef.current.clear();
        setSignatureDataUrl(null);
    };

    const saveSignature = () => {
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
            setSignatureDataUrl(signatureRef.current.toDataURL());
        }
    };

    return (
        <div className={styles.receiverSelect}>
            <div className={styles.typeButtons}>
                <button type="button" className={type === "client" ? "active" : ""} onClick={() => setType("client")}>
                    <i className="pi pi-user" />
                    Client
                </button>
                <button type="button" className={type === "agent" ? "active" : ""} onClick={() => setType("agent")}>
                    <i className="pi pi-id-card" />
                    Agent
                </button>
                <button type="button" className={type === "guest" ? "active" : ""} onClick={() => setType("guest")}>
                    <i className="pi pi-user-plus" />
                    Not a Customer
                </button>
            </div>

            {type === "client" && (
                <div className={styles.clientConfirm}>
                    <div className={styles.clientConfirmIcon}>
                        <i className="pi pi-check" />
                    </div>
                    <div>
                        <strong>{clientName || "No client resolved"}</strong>
                        <span>Signing as the registered client for this group.</span>
                    </div>
                </div>
            )}

            {type === "agent" && (
                <div className={styles.field}>
                    <label>Select Agent</label>
                    <Dropdown
                        value={selectedAgent}
                        options={agents}
                        optionLabel="name"
                        filter
                        placeholder={agents.length ? "Select an agent..." : "This client has no registered agents"}
                        onChange={(e) => setSelectedAgent(e.value)}
                        style={{ width: "100%" }}
                    />
                </div>
            )}

            {type === "guest" && (
                <div className={styles.field}>
                    <label>Receiver Name</label>
                    <input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Name of person receiving the package" />
                </div>
            )}

            <div className={styles.field}>
                <label>Signature</label>
                {signatureDataUrl ? (
                    <div>
                        <img src={signatureDataUrl} alt="Signature preview" className={styles.signaturePreview} />
                        <Button label="Redo Signature" icon="pi pi-refresh" outlined onClick={() => setSignatureDataUrl(null)} style={{ marginTop: "0.75rem" }} />
                    </div>
                ) : (
                    <div>
                        <SignatureCanvas ref={signatureRef} canvasProps={{ className: styles.signatureCanvas }} />
                        <div className={styles.signatureActions}>
                            <Button label="Clear" icon="pi pi-times" outlined severity="danger" onClick={clearSignature} />
                            <Button label="Save Signature" icon="pi pi-check" onClick={saveSignature} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReceiverSelect;
